import HeaderTable from "./components/HeaderTable";
import { formatMoney } from "infra/helpers/number";
import {
  RelatorioDemonstrativoReceitaDespesaAnualResponse,
  FiltroRelatorioReceitaDespesaAnual,
} from "features/relatorios/typings";
import moment from "moment";
import CardDefault from "components/Cards/CardDefault";
import RelatorioSectionTitle from "ui/components/Relatorio/RelatorioSectionTitle";
import { OperacaoEnum } from "resources/enums/conta";
import colors from "resources/theme/colors";
import { flattenRelatorioItems } from "./helper/util";
import { Empty } from "antd";
import { useLoading } from "components/GlobalLoading";
import { Text, Flex } from "@chakra-ui/react";
import NewTable from "ui/components/NewTable";
import {
  formatterDemonstrativoReceitaDespesaAnual,
  TabelaCategoriasRelatorioDemonstrativoReceitaDespesaAnualRow,
} from "./helper/util";
import useTabelaCategoriasRelatorioDemonstrativoReceitaDespesaAnualColumns from "./hooks/useTabelaCategoriasColumns";
import NewTableStyles from "./components/NewTableStyles";

interface RelatorioDemonstrativoProps {
  onChangeFiltro: (v: Partial<FiltroRelatorioReceitaDespesaAnual>) => void;
  filtro: FiltroRelatorioReceitaDespesaAnual;
  data: RelatorioDemonstrativoReceitaDespesaAnualResponse | undefined;
  isLoading?: boolean;
}

function RelatorioDemonstrativo({
  filtro,
  data,
  isLoading,
  onChangeFiltro,
}: RelatorioDemonstrativoProps) {
  const flattenReceitas = flattenRelatorioItems(data?.receitas || [], 0);
  const flattenDespesas = flattenRelatorioItems(data?.despesas || [], 0);

  const receitasTabela = flattenReceitas.map(
    formatterDemonstrativoReceitaDespesaAnual
  );
  const despesasTabela = flattenDespesas.map(
    formatterDemonstrativoReceitaDespesaAnual
  );

  const columns =
    useTabelaCategoriasRelatorioDemonstrativoReceitaDespesaAnualColumns(
      filtro.ano
    );

  useLoading(!!isLoading);

  const dataInicio = `${filtro.ano}-01-01`;

  return (
    <CardDefault>
      <HeaderTable
        value={filtro.search || ""}
        onSearch={(v) => onChangeFiltro({ search: v })}
        onSelectOperacao={(operacao) => onChangeFiltro({ operacao })}
        operacao={filtro.operacao}
        filtro={filtro}
        receitas={flattenReceitas}
        despesas={flattenDespesas}
        transferencias={data?.transferencias}
        totais={data?.totais!}
      />

      <RelatorioSectionTitle
        containerProps={{ mt: 8, mb: 4 }}
        items={[
          {
            text: `Saldo em ${moment(dataInicio)
              .add(-1, "days")
              .format("DD/MM/YYYY")}${
              moment(dataInicio).add(-1, "days").isAfter(moment().endOf("day"))
                ? " (Previsto)"
                : ""
            }`,
            colSpan: 16,
            bold: true,
          },
          {
            text: `${formatMoney(data?.totais.saldoInicial || 0)}`,
            colSpan: 8,
            bold: true,
            textAlign: "right",
            color: colors.primary,
          },
        ]}
      />

      {filtro.operacao === OperacaoEnum.Receita || !filtro.operacao ? (
        <>
          <RelatorioSectionTitle
            containerProps={{ mt: 4, mb: 4 }}
            items={[
              {
                text: "Receitas",
                colSpan: 24,
                bold: true,
              },
            ]}
          />

          <NewTableStyles>
            <NewTable<TabelaCategoriasRelatorioDemonstrativoReceitaDespesaAnualRow>
              columns={columns}
              bordered
              dataSource={receitasTabela}
              hideShadow
              scroll={{
                x: 700,
                y: 525,
              }}
            />
          </NewTableStyles>

          {!isLoading && !receitasTabela.length ? (
            <Empty description="Sem contas categorias de receita para o filtro atual" />
          ) : null}
        </>
      ) : null}

      {filtro.operacao === OperacaoEnum.Despesa || !filtro.operacao ? (
        <>
          <RelatorioSectionTitle
            containerProps={{ mt: 8, mb: 4 }}
            items={[
              {
                text: "Despesas",
                colSpan: 24,
                bold: true,
              },
            ]}
          />

          <NewTableStyles>
            <NewTable<TabelaCategoriasRelatorioDemonstrativoReceitaDespesaAnualRow>
              columns={columns}
              bordered
              dataSource={despesasTabela}
              hideShadow
              scroll={{
                x: 700,
                y: 525,
              }}
            />
          </NewTableStyles>

          {!isLoading && !flattenDespesas.length ? (
            <Empty description="Sem contas categorias de despesa para o filtro atual" />
          ) : null}
        </>
      ) : null}

      {data?.transferencias?.length ? (
        <>
          <RelatorioSectionTitle
            containerProps={{ mt: 8, mb: 1 }}
            items={[
              {
                text: `Transferências`,
                colSpan: 16,
                bold: true,
              },
              {
                text: `Valor`,
                colSpan: 8,
                textAlign: "right",
                bold: true,
              },
            ]}
          />

          {data?.transferencias.map((transferencia) => (
            <Flex mt={4} key={transferencia.id}>
              <Text
                color={colors.black600}
                display="inline"
                fontWeight="medium"
              >
                {moment(transferencia.data).format("DD/MM/YYYY")} - De{" "}
                <Text
                  fontWeight="medium"
                  display="inline"
                  textDecoration="underline"
                >
                  {transferencia.contaOrigem}
                </Text>{" "}
                para{" "}
                <Text
                  fontWeight="medium"
                  display="inline"
                  textDecoration="underline"
                >
                  {transferencia.contaDestino}
                </Text>
              </Text>
              <Text display="inline" ml="auto">
                {formatMoney(transferencia.valor)}
              </Text>
            </Flex>
          ))}
        </>
      ) : null}

      <RelatorioSectionTitle
        containerProps={{ mt: 8, mb: 1 }}
        items={[
          {
            text: `Resultado (Receitas - Despesas)`,
            colSpan: 20,
            bold: true,
          },
          {
            text: `${formatMoney(data?.totais.resultado || 0)}`,
            colSpan: 4,
            bold: true,
            textAlign: "right",
            color: (data?.totais.resultado || 0) < 0 ? colors.red : undefined,
          },
        ]}
      />
    </CardDefault>
  );
}

export default RelatorioDemonstrativo;
